import type { FilterOption } from '@/scopes/search/types'
import { useIntl } from '@/intl'
import * as DepartureAirportFilterPrimitive from '@/scopes/common/components/Filters/DepartureAirportFilterPrimitive'

import * as DialogPrimitive from '@/scopes/search/components/QuickSearchBar/DialogPrimitive'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import {
  Button,
  ButtonLoaderIcon,
  Icon,
  SheetClose,
  SheetContentWrapper,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SpaceBetween,
  tk,
  Typography,
} from '@sunwebgroup/ui-components'
import * as React from 'react'
import { Item } from './QuickSearchBar'

export function DepartureAirportFilterComponent({
  disabled,
  onConfirm,
  options,
  selectedOptions,
}: {
  onConfirm: (selection: string[]) => void
  options: FilterOption<string>[]
  selectedOptions: FilterOption<string>[]
  disabled?: boolean
}) {
  const intl = useIntl()
  const formattedSelectedValues = selectedOptions.map(o => o.value).join(', ')
  const { setActiveItem } = useQuickSearchStore()
  const [localSelectedValues, setLocalSelectedValues] = React.useState(selectedOptions.map(option => option.value))
  const onSelectedValueChange = (newSelectedValue: string[]) => {
    setLocalSelectedValues(newSelectedValue)
  }

  React.useEffect(() => {
    setLocalSelectedValues(selectedOptions.map(option => option.value))
  }, [selectedOptions])

  const handleClose = () => {
    setActiveItem(null)
    onConfirm(localSelectedValues)
  }

  return (
    <Item.Root
      id="airport-filter"
      icon="IconAirplane"
      placeholder={intl.formatMessage({
        id: 'quicksearch_filter_departure_airport_placeholder',
      })}
      title={intl.formatMessage({
        id: 'quicksearch_filter_departure_airport_title',
      })}
      onClose={handleClose}
    >
      <Item.AsPopover selectionFormattedText={formattedSelectedValues} disabled={disabled} data-qa="departure-airport-filter">
        <SpaceBetween direction="y" spacing={tk('spacing_md')}>
          <DepartureAirportFilterPrimitive.Root
            options={options}
            selectedValues={localSelectedValues}
            onSelectedValueChange={onSelectedValueChange}
          />
          <DepartureAirportFilterPrimitive.Footer>
            <DepartureAirportFilterPrimitive.CloseButton $fullSize onClick={handleClose} disabled={disabled}>
              {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
            </DepartureAirportFilterPrimitive.CloseButton>
          </DepartureAirportFilterPrimitive.Footer>
        </SpaceBetween>
      </Item.AsPopover>
      <Item.AsSheet
        selectionFormattedText={formattedSelectedValues}
        disabled={disabled}
        data-qa="departure-airport-filter"
      >
        <>
          <SheetHeader>
            <SheetTitle>{intl.formatMessage({ id: 'quicksearch_filter_departure_airport_title' })}</SheetTitle>
            <SheetClose>{intl.formatMessage({ id: 'quicksearch_cancel_button_text' })}</SheetClose>
          </SheetHeader>
          <SheetContentWrapper>
            <DepartureAirportFilterPrimitive.Root
              options={options}
              selectedValues={localSelectedValues}
              onSelectedValueChange={onSelectedValueChange}
            />
          </SheetContentWrapper>
          <SheetFooter $isFixed={false}>
            <Button $fullSize onClick={handleClose} disabled={disabled}>
              {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
            </Button>
          </SheetFooter>
        </>
      </Item.AsSheet>
      <Item.AsDialog selectionFormattedText={formattedSelectedValues} disabled={disabled} data-qa="departure-airport-filter">
        <DialogPrimitive.Root>
          <DialogPrimitive.Header>
            <DialogPrimitive.HeaderTitle>
              <Typography $variant="body-lg-bold">
                {intl.formatMessage({ id: 'quicksearch_filter_departure_airport_title' })}
              </Typography>
              <Button onClick={handleClose} $hierarchy="tertiary" $withoutColor>
                <Icon glyph="IconCrossMedium" size="md" />
              </Button>
            </DialogPrimitive.HeaderTitle>
          </DialogPrimitive.Header>
          <DialogPrimitive.Body>
            <DepartureAirportFilterPrimitive.Root
              options={options}
              selectedValues={localSelectedValues}
              onSelectedValueChange={onSelectedValueChange}
            />
          </DialogPrimitive.Body>
          <DialogPrimitive.Footer>
            <Button $fullSize onClick={handleClose} disabled={disabled}>
              {disabled && <ButtonLoaderIcon />}
              {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
            </Button>
          </DialogPrimitive.Footer>
        </DialogPrimitive.Root>
      </Item.AsDialog>
      <Item.AsIcon
        selectionFormattedText={formattedSelectedValues}
        compactSelectionFormattedText={formattedSelectedValues}
      />
    </Item.Root>
  )
}
